import * as TutorialEngine from './krida-tutorial-engine.js';

let tutorialModal = null;
let videoElement = null;
let descriptionElement = null;
let prevBtn = null;
let nextBtn = null;
let closeBtn = null;
let videoContainer = null;
let isTutorialInitialized = false;

const TUTORIAL_STORAGE_KEY_PREFIX = 'kridaTutorialPlayed_';

const allTutorialsData = {
  // Full tutorial — composed from individual step paths
  fullTutorial: [
    '/tutorials/data/welcome.json',
    '/tutorials/data/cards-1a.json',
    '/tutorials/data/scoring-1a.json',
    '/tutorials/data/piles-1a.json',
    '/tutorials/data/sow.json',
    '/tutorials/data/sow-1a.json',
    '/tutorials/data/harvest.json',
    '/tutorials/data/harvest-2a.json',
    '/tutorials/data/harvest-2b.json',
    '/tutorials/data/harvest-2c.json',
    '/tutorials/data/stockpile-intro.json',
    '/tutorials/data/stockpile-3a.json',
    '/tutorials/data/stockpile-3b.json',
    '/tutorials/data/stockpile-3c.json',
    '/tutorials/data/scoring-1b.json',
    '/tutorials/data/scoring.json',
    '/tutorials/data/tutorial-complete.json'
  ],

  // Individual modules for hamburger menu replay
  sow:       ['/tutorials/data/sow.json', '/tutorials/data/sow-1a.json'],
  harvest:   ['/tutorials/data/harvest.json', '/tutorials/data/harvest-2a.json',
              '/tutorials/data/harvest-2b.json', '/tutorials/data/harvest-2c.json'],
  stockpile: ['/tutorials/data/stockpile-intro.json', '/tutorials/data/stockpile-3a.json',
              '/tutorials/data/stockpile-3b.json', '/tutorials/data/stockpile-3c.json'],
  scoring:   ['/tutorials/data/scoring.json'],

  // Legacy alias
  quickStart: [
    '/tutorials/data/welcome.json',
    '/tutorials/data/gameplay.json',
    '/tutorials/data/cards.json',
    '/tutorials/data/piles.json',
    '/tutorials/data/sow.json',
    '/tutorials/data/harvest.json',
    '/tutorials/data/stockpile-intro.json',
    '/tutorials/data/scoring.json',
    '/tutorials/data/summary-tips.json'
  ],
  advancedTutorials: [
    '/tutorials/data/strategies.json',
    '/tutorials/data/advanced_stockpiling_multi.json'
  ]
};

let currentTutorialName = null;
let currentStepJsonPaths = [];
let currentStepIndex = 0;
let currentIsAutoLaunch = false;
let scenarioActive = false; // True when an interactive scenario is running

function markTutorialAsPlayed(tutorialName) {
  if (!tutorialName) return;
  try {
    localStorage.setItem(TUTORIAL_STORAGE_KEY_PREFIX + tutorialName, 'true');
  } catch (e) {
    console.warn('localStorage is not available. Tutorial may show again.', e);
  }
}

function hasTutorialBeenPlayed(tutorialName) {
  if (!tutorialName) return false;
  try {
    return localStorage.getItem(TUTORIAL_STORAGE_KEY_PREFIX + tutorialName) === 'true';
  } catch (e) {
    console.warn('localStorage is not available. Assuming tutorial has not been played.', e);
    return false;
  }
}

async function updateTutorialStep() {
  if (!currentStepJsonPaths || !currentStepJsonPaths[currentStepIndex]) {
    console.error('Current tutorial step path not found.');
    if (descriptionElement) descriptionElement.innerHTML = '<p>Error: Tutorial step could not be loaded.</p>';
    if (videoContainer) videoContainer.style.display = 'none';
    if (videoElement) { videoElement.removeAttribute('src'); videoElement.removeAttribute('poster'); }
    return;
  }

  const stepJsonPath = currentStepJsonPaths[currentStepIndex];

  // Reset UI elements for the new step
  if (descriptionElement) descriptionElement.innerHTML = '<p>Loading step content...</p>';

  // Detach old listeners from videoElement by cloning and replacing it.
  if (videoElement && videoElement.parentNode) {
      const newVideoElement = videoElement.cloneNode(true);
      videoElement.parentNode.replaceChild(newVideoElement, videoElement);
      videoElement = newVideoElement;
  }
  if (videoElement) {
    videoElement.removeAttribute('src');
    videoElement.removeAttribute('poster');
  }
  if (videoContainer) videoContainer.style.display = 'none';

  try {
    const responseJson = await fetch(stepJsonPath);
    if (!responseJson.ok) {
      throw new Error(`Failed to load tutorial step JSON: ${stepJsonPath} (${responseJson.status})`);
    }
    const stepData = await responseJson.json();

    // Check if this is an interactive or card-spotlight step
    if (stepData.type === 'interactive' || stepData.type === 'card-spotlight') {
      _launchInteractiveStep(stepData);
      return;
    }

    // Standard content step — show wizard modal
    if (tutorialModal) {
      tutorialModal.classList.remove('hidden');
      tutorialModal.classList.add('active');
    }

    // 1. Load HTML content for the description
    if (stepData.contentHtml && descriptionElement) {
      try {
        const responseHtml = await fetch(stepData.contentHtml);
        if (!responseHtml.ok) {
          throw new Error(`Failed to load tutorial content HTML: ${stepData.contentHtml} (${responseHtml.status})`);
        }
        descriptionElement.innerHTML = await responseHtml.text();
      } catch (htmlError) {
        console.error('Error loading tutorial HTML content:', htmlError);
        descriptionElement.innerHTML = '<p>Error loading description for this step.</p>';
      }
    } else if (descriptionElement) {
      descriptionElement.innerHTML = stepData.description || '<p>No description provided for this step.</p>';
    }

    // 2. Attempt to load video and poster
    let showVideoArea = false;

    if (stepData.videoSrc && videoElement && videoContainer) {
      const videoLoadPromise = new Promise(resolve => {
        videoElement.onloadeddata = () => {
          showVideoArea = true;
          resolve(true);
        };
        videoElement.onerror = () => {
          resolve(false);
        };
        videoElement.src = stepData.videoSrc;
      });

      const posterLoadPromise = new Promise(resolve => {
        if (stepData.poster) {
          videoElement.poster = stepData.poster;
          const img = new Image();
          img.onload = () => {
            if (!showVideoArea) showVideoArea = true;
            resolve(true);
          };
          img.onerror = () => {
            videoElement.removeAttribute('poster');
            resolve(false);
          };
          img.src = stepData.poster;
        } else {
          resolve(false);
        }
      });

      await Promise.all([videoLoadPromise, posterLoadPromise]);
    }

    if (showVideoArea && videoContainer) {
      videoContainer.style.display = 'block';
    } else if (videoContainer) {
      videoContainer.style.display = 'none';
    }

  } catch (error) {
    console.error('Error updating tutorial step:', error);
    if (descriptionElement) descriptionElement.innerHTML = `<p>Error loading step data: ${error.message}.</p>`;
    if (videoContainer) videoContainer.style.display = 'none';
    if (videoElement) { videoElement.removeAttribute('src'); videoElement.removeAttribute('poster'); }
  }

  // Update button states
  if (prevBtn) prevBtn.disabled = currentStepIndex === 0;
  if (nextBtn) {
    nextBtn.disabled = false;
    if (currentStepIndex === currentStepJsonPaths.length - 1) {
      nextBtn.textContent = 'Finish';
    } else {
      nextBtn.textContent = 'Next';
    }
  }
}

async function _launchInteractiveStep(stepData) {
  scenarioActive = true;

  // Hide the wizard modal while the scenario runs
  if (tutorialModal) {
    tutorialModal.classList.add('hidden');
    tutorialModal.classList.remove('active');
  }

  // Listen for scenario completion (one-shot)
  const onComplete = () => {
    document.removeEventListener('krida-tutorial-scenario-complete', onComplete);
    scenarioActive = false;

    // Advance to next step
    if (currentStepIndex < currentStepJsonPaths.length - 1) {
      currentStepIndex++;
      updateTutorialStep();
    } else {
      closeTutorial();
    }
  };
  document.addEventListener('krida-tutorial-scenario-complete', onComplete);

  // Wire wizard nav into the engine's overlay buttons
  const navCallbacks = {
    canPrev: currentStepIndex > 0,
    onPrev: () => {
      document.removeEventListener('krida-tutorial-scenario-complete', onComplete);
      TutorialEngine.deactivate();
      scenarioActive = false;
      if (currentStepIndex > 0) {
        currentStepIndex--;
        updateTutorialStep();
      }
    },
    onClose: () => {
      document.removeEventListener('krida-tutorial-scenario-complete', onComplete);
      TutorialEngine.deactivate();
      scenarioActive = false;
      closeTutorial();
    }
  };

  // Activate the scenario engine (async — may need to create temp game area)
  await TutorialEngine.activate(stepData, navCallbacks);
}

function handleNext() {
  if (scenarioActive) return; // Don't navigate while scenario is running
  if (currentStepIndex < currentStepJsonPaths.length - 1) {
    currentStepIndex++;
    updateTutorialStep();
  } else {
    closeTutorial();
  }
}

function handlePrev() {
  if (scenarioActive) return;
  if (currentStepIndex > 0) {
    currentStepIndex--;
    updateTutorialStep();
  }
}

function handleClose() {
  if (scenarioActive) {
    // Deactivate the scenario engine first
    TutorialEngine.deactivate();
    scenarioActive = false;
  }
  closeTutorial();
}

export async function launchTutorial(tutorialName, isAutoLaunch = false) {
  if (!tutorialModal || !prevBtn || !nextBtn || !closeBtn) {
    console.warn('Tutorial modal elements not found. Tutorial cannot be launched.');
    return;
  }

  if (!allTutorialsData[tutorialName] || allTutorialsData[tutorialName].length === 0) {
    console.error(`Tutorial "${tutorialName}" not found or is empty.`);
    return;
  }

  // For auto-launch, check fullTutorial first, then legacy quickStart
  const checkName = tutorialName === 'fullTutorial' ? 'fullTutorial' : tutorialName;
  if (isAutoLaunch && hasTutorialBeenPlayed(checkName)) {
    tutorialModal.classList.add('hidden');
    tutorialModal.classList.remove('active');
    return;
  }

  currentTutorialName = tutorialName;
  currentStepJsonPaths = allTutorialsData[tutorialName];
  currentStepIndex = 0;
  currentIsAutoLaunch = isAutoLaunch;

  if (closeBtn) {
    closeBtn.textContent = isAutoLaunch ? 'Skip' : 'Close';
  }

  await updateTutorialStep();
  tutorialModal.classList.remove('hidden');
  tutorialModal.classList.add('active');
}

function closeTutorial() {
  if (tutorialModal) {
    tutorialModal.classList.add('hidden');
    tutorialModal.classList.remove('active');
  }
  if (videoElement) {
    videoElement.pause();
    videoElement.currentTime = 0;
    videoElement.removeAttribute('src');
    videoElement.removeAttribute('poster');
  }
  markTutorialAsPlayed(currentTutorialName);
  document.dispatchEvent(new CustomEvent('krida-tutorial-completed', {
    detail: { tutorialName: currentTutorialName, wasAutoLaunch: currentIsAutoLaunch }
  }));
}

export function initTutorialSystem() {
    if (isTutorialInitialized) {
        return;
    }

    tutorialModal = document.getElementById('tutorial-modal');
    videoElement = document.getElementById('tutorial-video');
    descriptionElement = document.getElementById('tutorial-description');
    prevBtn = document.getElementById('tutorial-prev-btn');
    nextBtn = document.getElementById('tutorial-next-btn');
    closeBtn = document.getElementById('tutorial-close-btn');
    videoContainer = document.getElementById('tutorial-video-container');

    if (prevBtn && nextBtn && closeBtn) {
        prevBtn.addEventListener('click', handlePrev);
        nextBtn.addEventListener('click', handleNext);
        closeBtn.addEventListener('click', handleClose);
    } else {
        console.warn('Tutorial navigation buttons not found on script load. Navigation will not work.');
    }

    isTutorialInitialized = true;
    console.log("Tutorial System Initialized.");
}
